import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';

export interface StoredSubmission {
  id: string;
  type: 'trap-check' | 'bait-station-check' | 'line-check' | 'river-track';
  data: any;
  timestamp: number;
  userId: string;
  synced: boolean;
}

export interface UserPreferences {
  showWelcome: boolean;
  emailNotifications: boolean;
  offlineMode: boolean;
  lastSyncTime: number;
}

@Injectable({
  providedIn: 'root'
})
export class CatchitStorageService {
  private _storage: Storage | null = null;
  private readonly STORAGE_PREFIX = 'catchit_';

  // Storage keys
  private readonly KEYS = {
    STORED_SUBMISSIONS: 'stored_submissions',
    USER_PREFERENCES: 'user_preferences',
    OFFLINE_DATA: 'offline_data',
    LAST_SYNC: 'last_sync',
    USER_PROFILE_CACHE: 'user_profile_cache',
    APP_SETTINGS: 'app_settings'
  };

  constructor(private storage: Storage) {
    void this.init();
  }

  async init() {
    const storage = await this.storage.create();
    this._storage = storage;
  }

  private getPrefixedKey(key: string): string {
    return `${this.STORAGE_PREFIX}${key}`;
  }

  // Generic storage methods
  public async set(key: string, value: any): Promise<any> {
    return this._storage?.set(this.getPrefixedKey(key), value);
  }

  public async get(key: string): Promise<any> {
    return this._storage?.get(this.getPrefixedKey(key));
  }

  public async remove(key: string): Promise<any> {
    return this._storage?.remove(this.getPrefixedKey(key));
  }

  public async clear(): Promise<void> {
    return this._storage?.clear();
  }

  public async keys(): Promise<string[]> {
    const allKeys = await this._storage?.keys() || [];
    return allKeys
      .filter(key => key.startsWith(this.STORAGE_PREFIX))
      .map(key => key.substring(this.STORAGE_PREFIX.length));
  }

  // Stored submissions management
  public async getStoredSubmissions(): Promise<StoredSubmission[]> {
    const submissions = await this.get(this.KEYS.STORED_SUBMISSIONS);
    return submissions || [];
  }

  public async addStoredSubmission(submission: Omit<StoredSubmission, 'id' | 'timestamp'>): Promise<string> {
    const submissions = await this.getStoredSubmissions();
    const newSubmission: StoredSubmission = {
      ...submission,
      id: this.generateId(),
      timestamp: Date.now()
    };

    submissions.push(newSubmission);
    await this.set(this.KEYS.STORED_SUBMISSIONS, submissions);
    return newSubmission.id;
  }

  public async markSubmissionAsSynced(id: string): Promise<boolean> {
    const submissions = await this.getStoredSubmissions();
    const submission = submissions.find(sub => sub.id === id);

    if (!submission) {
      return false;
    }

    submission.synced = true;
    await this.set(this.KEYS.STORED_SUBMISSIONS, submissions);
    return true;
  }

  public async getUnsyncedSubmissions(): Promise<StoredSubmission[]> {
    const submissions = await this.getStoredSubmissions();
    return submissions.filter(sub => !sub.synced);
  }

  public async removeStoredSubmission(id: string): Promise<boolean> {
    const submissions = await this.getStoredSubmissions();
    const filteredSubmissions = submissions.filter(sub => sub.id !== id);

    if (filteredSubmissions.length === submissions.length) {
      return false; // No submission found with that ID
    }

    await this.set(this.KEYS.STORED_SUBMISSIONS, filteredSubmissions);
    return true;
  }

  public async storeSubmission(submission: any): Promise<string> {
    // Wrapper method for compatibility with original code
    return this.addStoredSubmission(submission);
  }

  // User preferences management
  public async getUserPreferences(): Promise<UserPreferences> {
    const preferences = await this.get(this.KEYS.USER_PREFERENCES);
    return preferences || {
      showWelcome: true,
      emailNotifications: true,
      offlineMode: false,
      lastSyncTime: 0
    };
  }

  public async setUserPreferences(preferences: Partial<UserPreferences>): Promise<void> {
    const currentPreferences = await this.getUserPreferences();
    const updatedPreferences = { ...currentPreferences, ...preferences };
    await this.set(this.KEYS.USER_PREFERENCES, updatedPreferences);
  }

  // Offline data management
  public async setOfflineData(key: string, data: any): Promise<void> {
    const offlineData = await this.getOfflineData();
    offlineData[key] = data;
    await this.set(this.KEYS.OFFLINE_DATA, offlineData);
  }

  public async getOfflineData(): Promise<{ [key: string]: any }> {
    return await this.get(this.KEYS.OFFLINE_DATA) || {};
  }

  public async getOfflineDataByKey(key: string): Promise<any> {
    const offlineData = await this.getOfflineData();
    return offlineData[key];
  }

  // Sync management
  public async setLastSyncTime(timestamp: number): Promise<void> {
    await this.set(this.KEYS.LAST_SYNC, timestamp);
  }

  public async getLastSyncTime(): Promise<number> {
    return await this.get(this.KEYS.LAST_SYNC) || 0;
  }

  // User profile caching
  public async cacheUserProfile(userId: string, profile: any): Promise<void> {
    const cache = await this.get(this.KEYS.USER_PROFILE_CACHE) || {};
    cache[userId] = {
      profile,
      timestamp: Date.now()
    };
    await this.set(this.KEYS.USER_PROFILE_CACHE, cache);
  }

  public async getCachedUserProfile(userId: string, maxAge: number = 300000): Promise<any> {
    const cache = await this.get(this.KEYS.USER_PROFILE_CACHE) || {};
    const cachedEntry = cache[userId];

    if (!cachedEntry) {
      return null;
    }

    const isExpired = Date.now() - cachedEntry.timestamp > maxAge;
    return isExpired ? null : cachedEntry.profile;
  }

  // Utility methods
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }
}